import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as TestIcon,
  VpnKey as KeyIcon,
  DateRange as DateIcon,
} from "@mui/icons-material";
import prepbusinessApi from "../../../../services/api/prepbusiness/prepbusiness";

const PrepBusinessCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    api_key: "",
    merchant_id: "",
    base_url: "https://fulfy.prepbusiness.com/api",
    is_active: true,
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await prepbusinessApi.getAll();
      setCredentials(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Failed to load PrepBusiness credentials:", err);
      setError("Failed to load credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (credential = null) => {
    if (credential) {
      setEditingId(credential.id);
      setFormData({
        api_key: "", // Don't populate API Key for security
        merchant_id: credential.merchant_id || "",
        base_url: credential.base_url || "https://fulfy.prepbusiness.com/api",
        is_active:
          credential.is_active !== undefined ? credential.is_active : true,
      });
    } else {
      setEditingId(null);
      setFormData({
        api_key: "",
        merchant_id: "",
        base_url: "https://fulfy.prepbusiness.com/api",
        is_active: true,
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    // API Key required if new
    if (!editingId && !formData.api_key) {
      setError("Please fill in the API Key.");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Only include api_key if user entered one
        const dataToSend = { ...formData };
        if (!dataToSend.api_key) delete dataToSend.api_key;

        await prepbusinessApi.update(editingId, dataToSend);
        setSuccess("Credential updated successfully.");
      } else {
        await prepbusinessApi.create(formData);
        setSuccess("Credential created successfully.");
      }
      handleCloseDialog();
      loadCredentials();
    } catch (err) {
      console.error("Error saving credential:", err);
      setError(err.response?.data?.detail || "Failed to save credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?"))
      return;

    setLoading(true);
    try {
      await prepbusinessApi.delete(id);
      setSuccess("Credential deleted successfully.");
      loadCredentials();
    } catch (err) {
      console.error("Error deleting credential:", err);
      setError("Failed to delete credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id) => {
    setLoading(true);
    try {
      const response = await prepbusinessApi.testSaved(id);
      if (response.data?.status === "success") {
        setSuccess("Connection successful!");
      } else {
        setError(
          `Connection failed: ${response.data?.message || "Unknown error"}`,
        );
      }
    } catch (err) {
      console.error("Test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTestForm = async () => {
    if (!formData.api_key) {
      setError("Please fill in the API Key to test.");
      return;
    }

    setLoading(true);
    try {
      const response = await prepbusinessApi.test(formData);
      if (response.data?.status === "success") {
        setSuccess("Connection successful! Credentials are valid.");
      } else {
        setError(
          `Connection failed: ${response.data?.message || "Unknown error"}`,
        );
      }
    } catch (err) {
      console.error("Form test failed:", err);
      setError(err.response?.data?.detail || "Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await prepbusinessApi.toggleActive(id);
      loadCredentials();
    } catch (err) {
      console.error("Error toggling active status:", err);
      setError("Failed to toggle active status.");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">PrepBusiness Credentials</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Credentials
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How to Get Your PrepBusiness Credentials:</strong>
        </Typography>
        <ol style={{ margin: 0, paddingLeft: "20px" }}>
          <li>
            Go to{" "}
            <a
              href="https://fulfy.prepbusiness.com/settings#api"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              PrepBusiness API Settings
            </a>
          </li>
          <li>Create a new token</li>
          <li>Insert it to the dialog</li>
        </ol>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {loading && (
        <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
      )}

      {!loading && credentials.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No PrepBusiness credentials found. Add one to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label="prepbusiness credentials table"
          >
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>API Key (Masked)</strong>
                </TableCell>
                <TableCell>
                  <strong>Merchant ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Base URL</strong>
                </TableCell>
                <TableCell>
                  <strong>Created At</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.map((cred) => (
                <TableRow
                  key={cred.id}
                  hover
                  sx={{
                    bgcolor: cred.is_active ? "action.selected" : "inherit",
                  }}
                >
                  <TableCell component="th" scope="row">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cred.is_active || false}
                          onChange={() => handleToggleActive(cred.id)}
                          name="is_active"
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        cred.is_active ? (
                          <Chip
                            label="Active"
                            color="primary"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Inactive
                          </Typography>
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <KeyIcon color="action" fontSize="small" />
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {cred.api_key ? `...${cred.api_key.slice(-8)}` : "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{cred.merchant_id || "N/A"}</TableCell>
                  <TableCell
                    sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                  >
                    {cred.base_url}
                  </TableCell>
                  <TableCell>
                    {cred.created_at && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <DateIcon fontSize="small" color="action" />
                        {new Date(cred.created_at).toLocaleDateString()}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test Connection">
                      <IconButton
                        size="small"
                        onClick={() => handleTest(cred.id)}
                        color="info"
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(cred)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(cred.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId ? "Edit Credentials" : "Add Credentials"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required={!editingId}
              fullWidth
              label="API Key"
              name="api_key"
              value={formData.api_key}
              onChange={handleChange}
              type="password"
              placeholder={
                editingId
                  ? "Leave blank to keep unchanged"
                  : "Enter PrepBusiness API Key"
              }
              helperText={
                editingId
                  ? "Only enter if you want to update the key"
                  : "Required for new accounts"
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Merchant ID (Optional)"
              name="merchant_id"
              value={formData.merchant_id}
              onChange={handleChange}
              placeholder="e.g. 16934"
              helperText="Required for merchant accounts (X-Selected-Client-Id)"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Base URL"
              name="base_url"
              value={formData.base_url}
              onChange={handleChange}
              helperText="Default: https://fulfy.prepbusiness.com/api"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestForm}
            color="info"
            disabled={loading || !formData.api_key}
          >
            Test Key
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrepBusinessCredentials;
